import { useQueryClient } from "@tanstack/react-query";
import { FileText, Info, KeyRound, Mail, MailCheck, Pencil, Plus, RotateCcwKey, Send, ShieldCheck, ShieldEllipsis, Trash2, UserPlus } from "lucide-react";
import { Tabs } from "radix-ui";
import { useState } from "react";
import { useNavigate, useParams } from "react-router";
import { ApiRequestError, useApi } from "../api/client";
import { qk, useApiTokens, useAudit, useInvalidateOrg, useInvitations, useNotificationChannels, useSession, useUsers } from "../api/queries";
import type { ApiTokenCreated, InvitationCreated, NotificationChannel, OrgRole, Session, User } from "../api/types";
import { LocaleMenu } from "../app/LocaleMenu";
import { API_MODE } from "../app/mode";
import { common } from "../i18n/common";
import { LOCALE_NAMES, LOCALES, useLocale, useT, type Locale } from "../i18n/core";
import { useFormat } from "../lib/format";
import { CodeBlock, ErrorState, LoadingRows, PageHead, Panel } from "../ui/Common";
import { Dialog } from "../ui/Dialog";
import { Pill } from "../ui/Pills";
import { useToast } from "../ui/Toasts";
import { CredentialsTab } from "./Credentials";
import { settingsMessages } from "./settings.i18n";

const ORG_ROLES: OrgRole[] = ["org_admin", "operator", "viewer"];
const TOKEN_DAYS = [30, 90, 180, 365] as const;

const errorText = (err: unknown, fallback: string) => (err instanceof ApiRequestError ? err.message : fallback);

export function SettingsPage() {
  const { orgId = "" } = useParams();
  const t = useT(settingsMessages);

  return (
    <>
      <PageHead title={t.title} lede={t.lede} />
      <AccountPanel />
      <section className="panel">
        <Tabs.Root defaultValue="users">
          <Tabs.List className="tabs-list" aria-label={t.tabs}>
            <Tabs.Trigger value="users">{t.tabUsers}</Tabs.Trigger>
            <Tabs.Trigger value="api">{t.tabApi}</Tabs.Trigger>
            <Tabs.Trigger value="notifications">{t.tabNotifications}</Tabs.Trigger>
            <Tabs.Trigger value="credentials">{t.tabCredentials}</Tabs.Trigger>
            <Tabs.Trigger value="audit">{t.tabAudit}</Tabs.Trigger>
          </Tabs.List>
          <Tabs.Content value="users"><UsersTab orgId={orgId} /></Tabs.Content>
          <Tabs.Content value="api"><TokensTab orgId={orgId} /></Tabs.Content>
          <Tabs.Content value="notifications"><NotificationsTab orgId={orgId} /></Tabs.Content>
          {/* Catálogo de credenciais de rede (ADR 0030). */}
          <Tabs.Content value="credentials"><CredentialsTab orgId={orgId} /></Tabs.Content>
          <Tabs.Content value="audit"><AuditTab orgId={orgId} /></Tabs.Content>
        </Tabs.Root>
      </section>
    </>
  );
}

// ---------------------------------------------------------------------------
// Sua conta

function AccountPanel() {
  const t = useT(settingsMessages);
  const tc = useT(common);
  const session = useSession();
  const user = session.data?.user;
  const [dialog, setDialog] = useState<"name" | "email" | "password" | null>(null);
  const close = () => setDialog(null);
  return (
    <Panel title={t.accountTitle}>
      <div className="form-grid">
        <div className="field">
          <span className="label">{t.name}</span>
          <div className="account-value">
            <span>{user?.name}</span>
            <button className="btn btn-sm" type="button" onClick={() => setDialog("name")} disabled={!user}><Pencil aria-hidden="true" /> {t.editName}</button>
          </div>
          <span className="hint">{t.nameHint}</span>
        </div>
        <div className="field">
          <span className="label">{t.email}</span>
          <div className="account-value">
            <span>{user?.email}</span>
            <button className="btn btn-sm" type="button" onClick={() => setDialog("email")} disabled={!user}><Mail aria-hidden="true" /> {t.changeEmail}</button>
          </div>
          <span className="hint">{t.emailHint}</span>
        </div>
        <div className="field">
          <span className="label">{tc.language}</span>
          <div style={{ justifySelf: "start" }}><LocaleMenu variant="plain" /></div>
          <span className="hint">{t.languageHint}</span>
        </div>
        <div className="field">
          <span className="label">{t.passwordLabel}</span>
          <div style={{ justifySelf: "start" }}>
            <button className="btn btn-sm" type="button" onClick={() => setDialog("password")}><KeyRound aria-hidden="true" /> {t.changePassword}</button>
          </div>
          <span className="hint">{t.passwordHint}</span>
        </div>
      </div>
      {dialog === "name" && user && <EditNameDialog current={user.name} onClose={close} />}
      {dialog === "email" && <ChangeEmailDialog onClose={close} />}
      {dialog === "password" && <ChangePasswordDialog onClose={close} />}
    </Panel>
  );
}

/** Listas de pessoas e auditoria das organizações em cache mostram nome e e-mail da conta. */
const isPeopleQuery = (key: readonly unknown[]) => key[0] === "org" && (key[2] === "users" || key[2] === "audit");

function EditNameDialog({ current, onClose }: { current: string; onClose: () => void }) {
  const t = useT(settingsMessages);
  const tc = useT(common);
  const api = useApi();
  const qc = useQueryClient();
  const toast = useToast();
  const [name, setName] = useState(current);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const saved = await api.updateProfile({ name: name.trim() });
      qc.setQueryData<Session>(qk.session, (s) => s && { ...s, user: { ...s.user, name: saved.name } });
      void qc.invalidateQueries({ predicate: (q) => isPeopleQuery(q.queryKey) });
      toast("ok", t.nameSaved);
      onClose();
    } catch (err) {
      setError(errorText(err, t.actionFailed));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={t.editName}
      description={t.nameHint}
      footer={
        <>
          <button className="btn" type="button" onClick={onClose}>{tc.cancel}</button>
          <button className="btn btn-primary" type="submit" form="name-form" disabled={busy || !name.trim()}>{tc.save}</button>
        </>
      }
    >
      <form id="name-form" onSubmit={submit} style={{ display: "grid", gap: 14 }} noValidate>
        <div className="field">
          <label htmlFor="profile-name">{t.name}</label>
          <input id="profile-name" className="input" autoComplete="name" value={name} maxLength={120} onChange={(e) => setName(e.target.value)} required autoFocus />
        </div>
        {error && <p className="field error" role="alert">{error}</p>}
      </form>
    </Dialog>
  );
}

function ChangeEmailDialog({ onClose }: { onClose: () => void }) {
  const t = useT(settingsMessages);
  const tc = useT(common);
  const api = useApi();
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const [newEmail, setNewEmail] = useState("");
  const [current, setCurrent] = useState("");
  const [code, setCode] = useState("");
  const [pending, setPending] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const email = newEmail.trim();

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await api.changeEmail({ newEmail: email, currentPassword: current, code });
      setCurrent("");
      setCode("");
      if (r.status === "pending") {
        // Nada muda na conta até a confirmação pelo link enviado ao endereço novo.
        setPending(r.email);
        return;
      }
      qc.setQueryData<Session>(qk.session, (s) => s && { ...s, user: { ...s.user, email: r.email } });
      void qc.invalidateQueries({ predicate: (q) => isPeopleQuery(q.queryKey) });
      toast("ok", t.emailChanged(r.email));
      onClose();
    } catch (err) {
      setError(errorText(err, t.actionFailed));
    } finally {
      setBusy(false);
    }
  }

  if (pending) {
    return (
      <Dialog
        open
        onOpenChange={(v) => !v && onClose()}
        title={t.emailPendingTitle}
        description={<span style={{ overflowWrap: "anywhere" }}>{t.emailPending(pending)}</span>}
        footer={
          <>
            {API_MODE === "mock" && (
              <button className="btn" type="button" onClick={() => navigate({ pathname: "/confirm-email", hash: "#ebe1_demo" })}>{t.mockConfirmEmail}</button>
            )}
            <button className="btn btn-primary" type="button" onClick={onClose}>{t.done}</button>
          </>
        }
      >
        <div className="callout" role="status"><MailCheck aria-hidden="true" /><span>{t.emailPendingNotice}</span></div>
      </Dialog>
    );
  }

  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={t.changeEmail}
      description={t.changeEmailDescription}
      footer={
        <>
          <button className="btn" type="button" onClick={onClose}>{tc.cancel}</button>
          <button className="btn btn-primary" type="submit" form="email-form" disabled={busy || !email.includes("@") || !current || code.length !== 6}>
            <Mail aria-hidden="true" /> {t.changeEmail}
          </button>
        </>
      }
    >
      <form id="email-form" onSubmit={submit} style={{ display: "grid", gap: 14 }} noValidate>
        <div className="field">
          <label htmlFor="email-new">{t.newEmail}</label>
          <input id="email-new" className="input" type="email" autoComplete="email" maxLength={254} value={newEmail} onChange={(e) => setNewEmail(e.target.value)} required autoFocus />
        </div>
        <div className="field">
          <label htmlFor="email-password">{t.currentPassword}</label>
          <input id="email-password" className="input" type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} required />
        </div>
        <div className="field">
          <label htmlFor="email-code">{t.code}</label>
          <input id="email-code" className="input code" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} required />
        </div>
        {error && <p className="field error" role="alert">{error}</p>}
      </form>
    </Dialog>
  );
}

function ChangePasswordDialog({ onClose }: { onClose: () => void }) {
  const t = useT(settingsMessages);
  const tc = useT(common);
  const api = useApi();
  const toast = useToast();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (next !== confirm) {
      setError(t.mismatch);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.changePassword({ currentPassword: current, newPassword: next, code });
      toast("ok", t.passwordChanged);
      onClose();
    } catch (err) {
      setError(errorText(err, t.actionFailed));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={t.changePassword}
      description={t.passwordHint}
      footer={
        <>
          <button className="btn" type="button" onClick={onClose}>{tc.cancel}</button>
          <button className="btn btn-primary" type="submit" form="pw-form" disabled={busy || code.length !== 6}>{t.changePassword}</button>
        </>
      }
    >
      <form id="pw-form" onSubmit={submit} style={{ display: "grid", gap: 14 }} noValidate>
        <div className="field">
          <label htmlFor="pw-current">{t.currentPassword}</label>
          <input id="pw-current" className="input" type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} required />
        </div>
        <div className="field">
          <label htmlFor="pw-new">{t.newPassword}</label>
          <input id="pw-new" className="input" type="password" autoComplete="new-password" minLength={12} maxLength={256} value={next} onChange={(e) => setNext(e.target.value)} required />
          <span className="hint">{t.newPasswordHint}</span>
        </div>
        <div className="field">
          <label htmlFor="pw-confirm">{t.confirmPassword}</label>
          <input id="pw-confirm" className="input" type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
        </div>
        <div className="field">
          <label htmlFor="pw-code">{t.code}</label>
          <input id="pw-code" className="input code" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} required />
        </div>
        {error && <p className="field error" role="alert">{error}</p>}
      </form>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Usuários e convites

function UsersTab({ orgId }: { orgId: string }) {
  const t = useT(settingsMessages);
  const tc = useT(common);
  const f = useFormat();
  const api = useApi();
  const toast = useToast();
  const invalidate = useInvalidateOrg();
  const session = useSession();
  const users = useUsers(orgId);
  const invitations = useInvitations(orgId);
  const [inviting, setInviting] = useState(false);
  const [removing, setRemoving] = useState<User | null>(null);
  const [resetting, setResetting] = useState<User | null>(null);
  const [busy, setBusy] = useState(false);
  const pending = invitations.data?.filter((i) => i.status === "pending") ?? [];

  async function changeRole(u: User, role: OrgRole) {
    try {
      await api.updateMember(orgId, u.id, role);
      toast("ok", t.roleChanged(u.name));
    } catch (err) {
      toast("error", errorText(err, t.actionFailed));
    } finally {
      invalidate(orgId);
    }
  }

  async function remove() {
    if (!removing) return;
    setBusy(true);
    try {
      await api.removeMember(orgId, removing.id);
      toast("ok", t.removed(removing.name));
      setRemoving(null);
    } catch (err) {
      toast("error", errorText(err, t.actionFailed));
    } finally {
      setBusy(false);
      invalidate(orgId);
    }
  }

  async function resetTotp() {
    if (!resetting) return;
    setBusy(true);
    try {
      await api.resetMemberTotp(orgId, resetting.id);
      toast("ok", t.totpResetDone(resetting.name));
      setResetting(null);
    } catch (err) {
      toast("error", errorText(err, t.actionFailed));
    } finally {
      setBusy(false);
      invalidate(orgId);
    }
  }

  async function revoke(id: string) {
    try {
      await api.revokeInvitation(orgId, id);
      toast("ok", t.invitationRevoked);
    } catch (err) {
      toast("error", errorText(err, t.actionFailed));
    } finally {
      invalidate(orgId);
    }
  }

  return (
    <>
      <div className="panel-head" style={{ borderBottom: 0 }}>
        <span className="sub">{t.usersSub}</span>
        <button className="btn btn-sm" type="button" onClick={() => setInviting(true)}><UserPlus aria-hidden="true" /> {t.invite}</button>
      </div>

      {pending.length > 0 && (
        <div className="panel-body" style={{ paddingTop: 0 }}>
          <div className="eyebrow" style={{ marginBottom: 6 }}>{t.pendingTitle}</div>
          <ul className="plain-list">
            {pending.map((i) => (
              <li key={i.id} style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", padding: "6px 0" }}>
                <strong>{i.name}</strong>
                <span className="ink-2">{i.email}</span>
                <span className="tag">{tc.role[i.role]}</span>
                <span className="muted" style={{ fontSize: "var(--fs-12)" }}>{t.invitedBy(i.createdBy)} · {t.expiresIn(f.ago(i.expiresAt))}</span>
                <button className="btn btn-sm btn-ghost btn-danger" style={{ marginLeft: "auto" }} type="button" onClick={() => void revoke(i.id)}>{t.revoke}</button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {users.isPending ? <LoadingRows rows={4} /> : users.isError ? <div className="panel-body"><ErrorState error={users.error} /></div> : (
        <div className="table-wrap">
          <table className="data">
            <thead><tr><th>{t.colName}</th><th>{t.colRole}</th><th>{t.colTwoStep}</th><th>{t.colLastLogin}</th><th><span className="sr-only">{t.actions}</span></th></tr></thead>
            <tbody>
              {users.data?.map((u) => {
                const self = u.id === session.data?.user.id;
                const editable = !self && !u.inherited && u.role !== "platform_admin";
                // Mesma regra de papel e remoção; administrador da plataforma só por outro (ADR 0020).
                // Sem autenticador cadastrado, não há o que redefinir.
                const canResetTotp = u.totpEnabled && !self && !u.inherited && (!u.platformAdmin || session.data?.user.platformAdmin === true);
                return (
                  <tr key={u.id}>
                    <td>
                      <div className="host">
                        <strong style={{ fontFamily: "var(--font-ui)", fontWeight: 600 }}>{u.name}{self && <span className="muted"> ({t.you})</span>}</strong>
                        <span>{u.email}</span>
                      </div>
                    </td>
                    <td>
                      {editable ? (
                        <select className="select" style={{ height: 30 }} aria-label={t.changeRoleLabel(u.name)} value={u.role} onChange={(e) => void changeRole(u, e.target.value as OrgRole)}>
                          {ORG_ROLES.map((r) => <option key={r} value={r}>{tc.role[r]}</option>)}
                        </select>
                      ) : (
                        <>{tc.role[u.role]}{u.inherited && <span className="muted"> · {t.inherited}</span>}</>
                      )}
                    </td>
                    <td>{u.totpEnabled ? <Pill tone="ok" icon={ShieldCheck}>{t.twoStepOn}</Pill> : <Pill tone="warn" icon={ShieldEllipsis}>{t.twoStepPending}</Pill>}</td>
                    <td className="num">{f.ago(u.lastLoginAt)}</td>
                    <td className="right">
                      {(editable || canResetTotp) && (
                        <div style={{ display: "inline-flex", gap: 2 }}>
                          {canResetTotp && (
                            <button className="btn btn-ghost btn-icon" type="button" aria-label={t.resetTotpLabel(u.name)} title={t.resetTotpLabel(u.name)} onClick={() => setResetting(u)}>
                              <RotateCcwKey aria-hidden="true" />
                            </button>
                          )}
                          {editable && (
                            <button className="btn btn-ghost btn-icon btn-danger" type="button" aria-label={t.removeLabel(u.name)} title={t.remove} onClick={() => setRemoving(u)}>
                              <Trash2 aria-hidden="true" />
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {inviting && <InviteDialog orgId={orgId} onClose={() => { setInviting(false); invalidate(orgId); }} />}
      <Dialog
        open={removing !== null}
        onOpenChange={(v) => !v && setRemoving(null)}
        title={removing ? t.removeTitle(removing.name) : ""}
        description={t.removeDescription}
        footer={
          <>
            <button className="btn" type="button" onClick={() => setRemoving(null)}>{tc.cancel}</button>
            <button className="btn btn-primary" style={{ background: "var(--crit-mark)", borderColor: "var(--crit-mark)" }} type="button" onClick={() => void remove()} disabled={busy}>
              {t.remove}
            </button>
          </>
        }
      />
      <Dialog
        open={resetting !== null}
        onOpenChange={(v) => !v && setResetting(null)}
        title={resetting ? t.resetTotpTitle(resetting.name) : ""}
        description={t.resetTotpDescription}
        footer={
          <>
            <button className="btn" type="button" onClick={() => setResetting(null)}>{tc.cancel}</button>
            <button className="btn btn-primary" style={{ background: "var(--crit-mark)", borderColor: "var(--crit-mark)" }} type="button" onClick={() => void resetTotp()} disabled={busy}>
              <RotateCcwKey aria-hidden="true" /> {t.resetTotpConfirm}
            </button>
          </>
        }
      >
        <ul className="consequences">
          <li>{t.resetTotpSessions}</li>
          <li>{t.resetTotpNextLogin}</li>
          <li>{t.resetTotpPassword}</li>
        </ul>
      </Dialog>
    </>
  );
}

function InviteDialog({ orgId, onClose }: { orgId: string; onClose: () => void }) {
  const t = useT(settingsMessages);
  const tc = useT(common);
  const api = useApi();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState<OrgRole>("operator");
  const [created, setCreated] = useState<InvitationCreated | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      setCreated(await api.createInvitation(orgId, { email, name, role }));
    } catch (err) {
      setError(errorText(err, t.actionFailed));
    } finally {
      setBusy(false);
    }
  }

  if (created) {
    const link = window.location.origin + (window.location.hash.startsWith("#/") ? "/#" : "") + created.acceptPath;
    return (
      <Dialog open onOpenChange={(v) => !v && onClose()} title={t.inviteCreatedTitle} description={t.inviteLinkHint(created.invitation.email)}
        footer={<button className="btn btn-primary" type="button" onClick={onClose}>{t.done}</button>}>
        <CodeBlock code={link} label={t.inviteLinkLabel} />
      </Dialog>
    );
  }

  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={t.invite}
      description={t.inviteDescription}
      footer={
        <>
          <button className="btn" type="button" onClick={onClose}>{tc.cancel}</button>
          <button className="btn btn-primary" type="submit" form="invite-form" disabled={busy}><UserPlus aria-hidden="true" /> {t.createInvite}</button>
        </>
      }
    >
      <form id="invite-form" onSubmit={submit} style={{ display: "grid", gap: 14 }}>
        <div className="field">
          <label htmlFor="inv-name">{t.name}</label>
          <input id="inv-name" className="input" value={name} maxLength={120} onChange={(e) => setName(e.target.value)} required autoFocus />
        </div>
        <div className="field">
          <label htmlFor="inv-email">{t.email}</label>
          <input id="inv-email" className="input" type="email" value={email} maxLength={320} onChange={(e) => setEmail(e.target.value)} required />
        </div>
        <div className="field">
          <label htmlFor="inv-role">{t.role}</label>
          <select id="inv-role" className="select" value={role} onChange={(e) => setRole(e.target.value as OrgRole)}>
            {ORG_ROLES.map((r) => <option key={r} value={r}>{tc.role[r]}</option>)}
          </select>
          <span className="hint">{t.roleHint[role]}</span>
        </div>
        {error && <p className="field error" role="alert">{error}</p>}
      </form>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Tokens de API

function TokensTab({ orgId }: { orgId: string }) {
  const t = useT(settingsMessages);
  const tc = useT(common);
  const f = useFormat();
  const api = useApi();
  const toast = useToast();
  const invalidate = useInvalidateOrg();
  const tokens = useApiTokens(orgId);
  const [creating, setCreating] = useState(false);
  const [revoking, setRevoking] = useState<{ id: string; name: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [now] = useState(() => Date.now());

  async function revoke() {
    if (!revoking) return;
    setBusy(true);
    try {
      await api.revokeApiToken(orgId, revoking.id);
      toast("ok", t.tokenRevoked);
      setRevoking(null);
    } catch (err) {
      toast("error", errorText(err, t.actionFailed));
    } finally {
      setBusy(false);
      invalidate(orgId);
    }
  }

  return (
    <>
      <div className="panel-head" style={{ borderBottom: 0 }}>
        <span className="sub">{t.apiSub}</span>
        <button className="btn btn-sm" type="button" onClick={() => setCreating(true)}><Plus aria-hidden="true" /> {t.createToken}</button>
      </div>
      {tokens.isPending ? <LoadingRows rows={3} /> : tokens.isError ? <div className="panel-body"><ErrorState error={tokens.error} /></div> : tokens.data?.length === 0 ? (
        <div className="empty">{t.noTokens}</div>
      ) : (
        <div className="table-wrap">
          <table className="data">
            <thead><tr><th>{t.colName}</th><th>{t.colPrefix}</th><th>{t.colRole}</th><th>{t.colCreatedBy}</th><th>{t.colLastUsed}</th><th>{t.colExpires}</th><th><span className="sr-only">{t.revoke}</span></th></tr></thead>
            <tbody>
              {tokens.data?.map((tok) => (
                <tr key={tok.id}>
                  <td><strong>{tok.name}</strong></td>
                  <td className="mono">{tok.prefix}…</td>
                  <td>{tc.role[tok.role]}</td>
                  <td className="ink-2">{tok.createdBy}</td>
                  <td className="num">{f.ago(tok.lastUsedAt)}</td>
                  <td className="num">{Date.parse(tok.expiresAt) <= now ? <Pill tone="warn">{t.expired}</Pill> : f.dateTime(tok.expiresAt)}</td>
                  <td className="right">
                    <button className="btn btn-ghost btn-icon btn-danger" type="button" aria-label={t.revokeTokenLabel(tok.name)} title={t.revoke} onClick={() => setRevoking({ id: tok.id, name: tok.name })}>
                      <Trash2 aria-hidden="true" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating && <CreateTokenDialog orgId={orgId} onClose={() => { setCreating(false); invalidate(orgId); }} />}
      <Dialog
        open={revoking !== null}
        onOpenChange={(v) => !v && setRevoking(null)}
        title={revoking ? t.revokeTokenTitle(revoking.name) : ""}
        description={t.revokeTokenDescription}
        footer={
          <>
            <button className="btn" type="button" onClick={() => setRevoking(null)}>{tc.cancel}</button>
            <button className="btn btn-primary" style={{ background: "var(--crit-mark)", borderColor: "var(--crit-mark)" }} type="button" onClick={() => void revoke()} disabled={busy}>
              {t.revoke}
            </button>
          </>
        }
      />
    </>
  );
}

function CreateTokenDialog({ orgId, onClose }: { orgId: string; onClose: () => void }) {
  const t = useT(settingsMessages);
  const tc = useT(common);
  const api = useApi();
  const [name, setName] = useState(t.tokenDefaultName);
  const [role, setRole] = useState<OrgRole>("viewer");
  const [days, setDays] = useState<number>(90);
  const [created, setCreated] = useState<ApiTokenCreated | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      setCreated(await api.createApiToken(orgId, { name, role, expiresInDays: days }));
    } catch (err) {
      setError(errorText(err, t.actionFailed));
    } finally {
      setBusy(false);
    }
  }

  if (created) {
    const example = `curl -H "Authorization: Bearer ${created.secret}" \\\n  ${window.location.origin}/api/v1/orgs/${orgId}/machines`;
    return (
      <Dialog open onOpenChange={(v) => !v && onClose()} title={t.tokenCreatedTitle} description={t.tokenSecretHint}
        footer={<button className="btn btn-primary" type="button" onClick={onClose}>{t.done}</button>}>
        <div style={{ display: "grid", gap: 12 }}>
          <CodeBlock code={created.secret} label={t.tokenSecretLabel} />
          <span className="hint">{t.tokenUsage}</span>
          <CodeBlock code={example} label="curl" />
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={t.createToken}
      description={t.apiSub}
      footer={
        <>
          <button className="btn" type="button" onClick={onClose}>{tc.cancel}</button>
          <button className="btn btn-primary" type="submit" form="token-form" disabled={busy}><Plus aria-hidden="true" /> {t.createToken}</button>
        </>
      }
    >
      <form id="token-form" onSubmit={submit} style={{ display: "grid", gap: 14 }}>
        <div className="field">
          <label htmlFor="tok-name">{t.tokenName}</label>
          <input id="tok-name" className="input" value={name} maxLength={80} onChange={(e) => setName(e.target.value)} required autoFocus />
          <span className="hint">{t.tokenNameHint}</span>
        </div>
        <div className="form-grid">
          <div className="field">
            <label htmlFor="tok-role">{t.role}</label>
            <select id="tok-role" className="select" value={role} onChange={(e) => setRole(e.target.value as OrgRole)}>
              {ORG_ROLES.map((r) => <option key={r} value={r}>{tc.role[r]}</option>)}
            </select>
          </div>
          <div className="field">
            <label htmlFor="tok-days">{t.validity}</label>
            <select id="tok-days" className="select" value={days} onChange={(e) => setDays(Number(e.target.value))}>
              {TOKEN_DAYS.map((d) => <option key={d} value={d}>{t.days(d)}</option>)}
            </select>
          </div>
        </div>
        <span className="hint">{t.roleHint[role]}</span>
        {error && <p className="field error" role="alert">{error}</p>}
      </form>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Notificações (ADR 0016)

function NotificationsTab({ orgId }: { orgId: string }) {
  const t = useT(settingsMessages);
  const tc = useT(common);
  const f = useFormat();
  const api = useApi();
  const toast = useToast();
  const invalidate = useInvalidateOrg();
  const q = useNotificationChannels(orgId);
  const [editing, setEditing] = useState<NotificationChannel | "new" | null>(null);
  const [removing, setRemoving] = useState<NotificationChannel | null>(null);
  const [testing, setTesting] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function test(c: NotificationChannel) {
    setTesting(c.id);
    try {
      await api.testNotificationChannel(orgId, c.id);
      toast("ok", t.testSent);
    } catch (err) {
      toast("error", errorText(err, t.actionFailed));
    } finally {
      setTesting(null);
      invalidate(orgId);
    }
  }

  // Resumo mensal agora: valida o canal sem esperar o dia 1º, ou reenvia (ADR 0028).
  async function sendSummary(c: NotificationChannel) {
    setTesting(c.id);
    try {
      await api.sendMonthlySummary(orgId, c.id);
      toast("ok", t.summarySent);
    } catch (err) {
      toast("error", errorText(err, t.actionFailed));
    } finally {
      setTesting(null);
      invalidate(orgId);
    }
  }

  async function remove() {
    if (!removing) return;
    setBusy(true);
    try {
      await api.deleteNotificationChannel(orgId, removing.id);
      toast("ok", t.channelDeleted);
      setRemoving(null);
    } catch (err) {
      toast("error", errorText(err, t.actionFailed));
    } finally {
      setBusy(false);
      invalidate(orgId);
    }
  }

  return (
    <>
      <div className="panel-head" style={{ borderBottom: 0 }}>
        <span className="sub">{t.notificationsSub}</span>
        <button className="btn btn-sm" type="button" onClick={() => setEditing("new")} disabled={!q.data}><Plus aria-hidden="true" /> {t.newChannel}</button>
      </div>
      {q.data && !q.data.emailAvailable && (
        <div className="panel-body" style={{ paddingTop: 0 }}>
          <div className="callout warn"><Info aria-hidden="true" /><span>{t.emailUnavailable}</span></div>
        </div>
      )}
      {q.isPending ? <LoadingRows rows={3} /> : q.isError ? <div className="panel-body"><ErrorState error={q.error} /></div> : q.data.channels.length === 0 ? (
        <div className="empty">{t.noChannels}</div>
      ) : (
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>{t.channelName}</th><th>{t.destination}</th><th>{t.minSeverity}</th><th>{t.channelLanguage}</th><th>{t.lastDelivery}</th>
                <th><span className="sr-only">{t.testChannel}</span></th>
              </tr>
            </thead>
            <tbody>
              {q.data.channels.map((c) => (
                <tr key={c.id}>
                  <td>
                    <div className="host">
                      <strong style={{ fontFamily: "var(--font-ui)", fontWeight: 600 }}>{c.name}</strong>
                      <span>{c.kind === "email" ? t.kindEmail : t.kindWebhook} · {c.enabled ? t.channelEnabled : t.channelDisabled}{c.monthlySummary && <> · {t.monthlySummaryTag}</>}</span>
                    </div>
                    {c.lastError && <div className="callout crit" style={{ marginTop: 6 }}><Info aria-hidden="true" /><span>{t.channelLastError(c.lastError)}</span></div>}
                  </td>
                  <td className="ink-2" style={{ overflowWrap: "anywhere", maxWidth: 320 }}>{c.kind === "email" ? c.emails.join(", ") : <span className="mono">{c.url}</span>}</td>
                  <td>{c.minSeverity === "critical" ? t.severityCritical : t.severityWarning}</td>
                  <td>{LOCALE_NAMES[c.locale]}</td>
                  <td className="num">{f.ago(c.lastDeliveryAt)}</td>
                  <td className="right">
                    <div style={{ display: "inline-flex", gap: 2 }}>
                      <button className="btn btn-sm" type="button" onClick={() => void test(c)} disabled={testing !== null}><Send aria-hidden="true" /> {t.testChannel}</button>
                      {c.kind === "email" && (
                        <button className="btn btn-ghost btn-icon" type="button" aria-label={t.sendSummary(c.name)} title={t.sendSummary(c.name)} onClick={() => void sendSummary(c)} disabled={testing !== null}><FileText aria-hidden="true" /></button>
                      )}
                      <button className="btn btn-ghost btn-icon" type="button" aria-label={t.editChannel(c.name)} title={t.editChannel(c.name)} onClick={() => setEditing(c)}><Pencil aria-hidden="true" /></button>
                      <button className="btn btn-ghost btn-icon btn-danger" type="button" aria-label={t.deleteChannelLabel(c.name)} title={t.deleteChannelLabel(c.name)} onClick={() => setRemoving(c)}><Trash2 aria-hidden="true" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && q.data && (
        <ChannelDialog orgId={orgId} channel={editing === "new" ? null : editing} emailAvailable={q.data.emailAvailable}
          onClose={(changed) => { setEditing(null); if (changed) invalidate(orgId); }} />
      )}
      <Dialog
        open={removing !== null}
        onOpenChange={(v) => !v && setRemoving(null)}
        title={removing ? t.deleteChannelTitle(removing.name) : ""}
        description={t.deleteChannelDescription}
        footer={
          <>
            <button className="btn" type="button" onClick={() => setRemoving(null)}>{tc.cancel}</button>
            <button className="btn btn-primary" style={{ background: "var(--crit-mark)", borderColor: "var(--crit-mark)" }} type="button" onClick={() => void remove()} disabled={busy}>{t.remove}</button>
          </>
        }
      />
    </>
  );
}

function ChannelDialog({ orgId, channel, emailAvailable, onClose }: {
  orgId: string; channel: NotificationChannel | null; emailAvailable: boolean; onClose: (changed: boolean) => void;
}) {
  const t = useT(settingsMessages);
  const tc = useT(common);
  const api = useApi();
  const toast = useToast();
  const locale = useLocale();
  const [kind, setKind] = useState<"email" | "webhook">(channel?.kind ?? (emailAvailable ? "email" : "webhook"));
  const [name, setName] = useState(channel?.name ?? "");
  const [emails, setEmails] = useState(channel?.emails.join("\n") ?? "");
  const [url, setUrl] = useState(channel?.url ?? "https://");
  const [minSeverity, setMinSeverity] = useState<"warning" | "critical">(channel?.minSeverity ?? "warning");
  const [channelLocale, setChannelLocale] = useState<Locale>(channel?.locale ?? locale);
  const [enabled, setEnabled] = useState(channel?.enabled ?? true);
  const [monthlySummary, setMonthlySummary] = useState(channel?.monthlySummary ?? false);
  const [secret, setSecret] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const input = {
      kind, name, minSeverity, locale: channelLocale, enabled,
      ...(kind === "email" ? { emails: emails.split(/[\n,;]+/).map((x) => x.trim()).filter(Boolean), monthlySummary } : { url: url.trim() }),
    };
    try {
      if (channel) {
        await api.updateNotificationChannel(orgId, channel.id, input);
        toast("ok", t.channelSaved);
        onClose(true);
      } else {
        const r = await api.createNotificationChannel(orgId, input);
        toast("ok", t.channelSaved);
        if (r.secret) setSecret(r.secret);
        else onClose(true);
      }
    } catch (err) {
      setError(errorText(err, t.actionFailed));
    } finally {
      setBusy(false);
    }
  }

  if (secret) {
    return (
      <Dialog open onOpenChange={(v) => !v && onClose(true)} title={t.webhookSecretTitle} description={t.webhookSecretHint}
        footer={<button className="btn btn-primary" type="button" onClick={() => onClose(true)}>{t.done}</button>}>
        <div style={{ display: "grid", gap: 12 }}>
          <CodeBlock code={secret} label={t.webhookSecretLabel} />
          <span className="hint">{t.webhookVerify}</span>
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose(false)}
      title={channel ? t.editChannel(channel.name) : t.newChannel}
      description={t.notificationsSub}
      footer={
        <>
          <button className="btn" type="button" onClick={() => onClose(false)}>{tc.cancel}</button>
          <button className="btn btn-primary" type="submit" form="channel-form" disabled={busy}>{tc.save}</button>
        </>
      }
    >
      <form id="channel-form" onSubmit={submit} style={{ display: "grid", gap: 14 }}>
        <div className="form-grid">
          <div className="field">
            <label htmlFor="ch-kind">{t.kind}</label>
            <select id="ch-kind" className="select" value={kind} disabled={channel !== null} onChange={(e) => setKind(e.target.value as "email" | "webhook")}>
              <option value="email" disabled={!emailAvailable}>{t.kindEmail}</option>
              <option value="webhook">{t.kindWebhook}</option>
            </select>
          </div>
          <div className="field">
            <label htmlFor="ch-name">{t.channelName}</label>
            <input id="ch-name" className="input" value={name} maxLength={80} placeholder={t.channelNamePlaceholder} onChange={(e) => setName(e.target.value)} required autoFocus />
          </div>
        </div>
        {kind === "email" && !emailAvailable && <div className="callout warn"><Info aria-hidden="true" /><span>{t.emailUnavailable}</span></div>}
        {kind === "email" ? (
          <div className="field">
            <label htmlFor="ch-emails">{t.emailsLabel}</label>
            <textarea id="ch-emails" className="textarea" rows={3} value={emails} onChange={(e) => setEmails(e.target.value)} />
            <span className="hint">{t.emailsHint}</span>
          </div>
        ) : (
          <div className="field">
            <label htmlFor="ch-url">{t.webhookUrl}</label>
            <input id="ch-url" className="input mono" value={url} placeholder="https://" onChange={(e) => setUrl(e.target.value)} />
            <span className="hint">{t.webhookUrlHint}</span>
          </div>
        )}
        <div className="form-grid">
          <div className="field">
            <label htmlFor="ch-severity">{t.minSeverity}</label>
            <select id="ch-severity" className="select" value={minSeverity} onChange={(e) => setMinSeverity(e.target.value as "warning" | "critical")}>
              <option value="warning">{t.severityWarning}</option>
              <option value="critical">{t.severityCritical}</option>
            </select>
          </div>
          <div className="field">
            <label htmlFor="ch-locale">{t.channelLanguage}</label>
            <select id="ch-locale" className="select" value={channelLocale} onChange={(e) => setChannelLocale(e.target.value as Locale)}>
              {LOCALES.map((l) => <option key={l} value={l}>{LOCALE_NAMES[l]}</option>)}
            </select>
          </div>
        </div>
        <label className="check" htmlFor="ch-enabled">
          <input id="ch-enabled" type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          <span>{t.enabledLabel}</span>
        </label>
        {kind === "email" && (
          <label className="check" htmlFor="ch-monthly">
            <input id="ch-monthly" type="checkbox" checked={monthlySummary} onChange={(e) => setMonthlySummary(e.target.checked)} />
            <span>{t.monthlySummaryLabel}<br /><span className="hint">{t.monthlySummaryHint}</span></span>
          </label>
        )}
        {error && <p className="field error" role="alert">{error}</p>}
      </form>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Auditoria

function AuditTab({ orgId }: { orgId: string }) {
  const t = useT(settingsMessages);
  const f = useFormat();
  const audit = useAudit(orgId);
  if (audit.isPending) return <LoadingRows rows={6} />;
  if (audit.isError) return <div className="panel-body"><ErrorState error={audit.error} /></div>;
  return (
    <div className="table-wrap">
      <table className="data">
        <thead><tr><th>{t.colWhen}</th><th>{t.colWho}</th><th>{t.colWhat}</th><th>{t.colTarget}</th><th>{t.colIp}</th></tr></thead>
        <tbody>
          {audit.data?.map((a) => (
            <tr key={a.id}>
              <td className="num" style={{ whiteSpace: "nowrap" }}>{f.dateTime(a.at)}</td>
              <td>{a.actor}</td>
              <td>{a.action}</td>
              <td className="ink-2">{a.target}</td>
              <td className="mono muted">{a.ip}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
